// Medication administration log (nurse-facing). Renders inside the closing
// section of a care program. The nurse can only pick medications that exist in
// the patient's authorized catalog. If she selects a med, time / via / dose /
// indication are pre-filled from the catalog defaults but can be overridden.

import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Pill, Plus, Trash2, ShieldCheck, ShieldAlert, Clock, Ban } from "lucide-react";
import { toast } from "sonner";
import { VIAS, INDICATIONS, indicationShort } from "@/lib/meds";
import {
  lastAdministeredByMed,
  pendingRxDoses,
  prnAvailability,
  formatHours,
  formatDelay,
  hmToMin,
} from "@/lib/med-alerts";

function nowHM() {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

function nowMinutes() {
  const d = new Date();
  return d.getHours() * 60 + d.getMinutes();
}

export default function MedicationAdminLog({
  catalog,                 // [] of {id, name, default_via, default_dose, default_indication, schedule_times[], min_interval_hours}
  given,                   // [] of {id, med_id, name, time, via, dose, indication, observations, administered_by}
  history,                 // [] of cross-program administered entries (sorted desc) with sortable_ts
  onChange,                // (newList) => void
  nurseName,
  patientName,
}) {
  const [picking, setPicking] = useState(false);
  const [selectedMedId, setSelectedMedId] = useState("");

  const catalogById = useMemo(() => {
    const m = {};
    for (const c of catalog) m[c.id] = c;
    return m;
  }, [catalog]);

  const lastByMed = useMemo(() => lastAdministeredByMed(history), [history]);

  // Combine current shift + history when computing the last administration for PRN
  const lastByMedCombined = useMemo(() => {
    const combined = { ...lastByMed };
    const todayDate = new Date();
    todayDate.setHours(0, 0, 0, 0);
    for (const g of given || []) {
      if (!g.med_id || !g.time) continue;
      const mm = hmToMin(g.time);
      if (mm == null) continue;
      const dt = new Date(todayDate);
      dt.setHours(Math.floor(mm / 60), mm % 60, 0, 0);
      const existing = combined[g.med_id];
      if (!existing?.date || dt > existing.date) {
        combined[g.med_id] = { ...g, date: dt };
      }
    }
    return combined;
  }, [lastByMed, given]);

  const pendingRx = useMemo(
    () => pendingRxDoses(catalog, given, nowMinutes()),
    [catalog, given]
  );

  const addFromCatalog = (medId) => {
    const c = catalogById[medId];
    if (!c) return;
    // PRN interval check
    const avail = prnAvailability(c, lastByMedCombined);
    if (!avail.allowed) {
      toast.error(
        `Bloqueado: ${c.name} - ultima dosis hace ${formatHours(avail.hoursSinceLast)}. Faltan ${formatHours(avail.hoursUntilNext)} para la siguiente.`
      );
      return;
    }
    const entry = {
      id: `${Date.now()}-${Math.random().toString(16).slice(2, 7)}`,
      med_id: c.id,
      name: c.name,
      time: nowHM(),
      via: c.default_via || "Oral",
      dose: c.default_dose || "",
      indication: c.default_indication || "rx",
      observations: "",
      administered_by: nurseName || "",
    };
    onChange([...(given || []), entry]);
    setSelectedMedId("");
    setPicking(false);
    toast.success(`${c.name} agregado al registro`);
  };

  const updateEntry = (id, patch) => {
    onChange((given || []).map((g) => (g.id === id ? { ...g, ...patch } : g)));
  };

  const removeEntry = (id) => {
    onChange((given || []).filter((g) => g.id !== id));
  };

  const noPatient = !(patientName || "").trim();
  const empty = catalog.length === 0;

  return (
    <div className="space-y-3" data-testid="med-admin-section">
      {/* Rx pending doses alert */}
      {pendingRx.length > 0 && (
        <div
          className="rounded-xl border-2 border-amber-400 bg-amber-50 p-3"
          data-testid="med-rx-pending-alert"
          role="alert"
        >
          <div className="mb-1.5 flex items-center gap-2">
            <Clock className="h-5 w-5 text-amber-700" />
            <p className="text-sm font-bold text-amber-900">
              Dosis Rx vencidas ({pendingRx.length})
            </p>
          </div>
          <ul className="space-y-0.5 pl-7 text-sm font-semibold text-amber-900">
            {pendingRx.map((p, i) => (
              <li
                key={i}
                data-testid={`med-rx-pending-${p.med.id}-${p.scheduled}`}
                className="flex flex-wrap items-center gap-1.5"
              >
                <span className="tabular-nums">{p.scheduled}</span>
                <span>-</span>
                <span>
                  {p.med.name}
                  {p.med.default_dose ? ` (${p.med.default_dose})` : ""}
                </span>
                <span
                  className={`rounded-full px-2 py-0.5 text-[10px] font-bold tabular-nums ${
                    p.delayMinutes >= 60
                      ? "bg-red-600 text-white"
                      : p.delayMinutes >= 15
                      ? "bg-amber-600 text-white"
                      : "bg-amber-200 text-amber-900"
                  }`}
                  data-testid={`med-rx-delay-${p.med.id}-${p.scheduled}`}
                >
                  retraso {formatDelay(p.delayMinutes)}
                </span>
              </li>
            ))}
          </ul>
          <p className="mt-2 text-[11px] text-amber-700">
            Registralas usando &quot;Registrar medicamento administrado&quot; al cumplirse cada hora.
          </p>
        </div>
      )}

      {/* Catalog banner */}
      <div
        className={`flex flex-col gap-2 rounded-xl border-2 p-3 sm:flex-row sm:items-center sm:justify-between ${
          empty ? "border-amber-300 bg-amber-50" : "border-blue-200 bg-blue-50"
        }`}
        data-testid="med-catalog-banner"
      >
        <div className="flex items-start gap-2">
          {empty ? (
            <ShieldAlert className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-700" />
          ) : (
            <ShieldCheck className="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-700" />
          )}
          <div className="min-w-0">
            <p className={`text-sm font-bold ${empty ? "text-amber-900" : "text-blue-900"}`}>
              {empty
                ? "Sin medicamentos autorizados para este paciente"
                : `${catalog.length} medicamento(s) autorizado(s)`}
            </p>
            <p className={`text-xs ${empty ? "text-amber-700" : "text-blue-700"}`}>
              {noPatient
                ? "Captura el nombre del paciente para ver su catalogo."
                : "Solo se pueden administrar medicamentos de este catalogo. El coordinador lo gestiona desde la seccion Pacientes."}
            </p>
          </div>
        </div>
      </div>

      {/* Existing administered records */}
      {(given || []).length > 0 && (
        <ul className="space-y-2" data-testid="med-given-list">
          {given.map((g) => (
            <li
              key={g.id}
              className="rounded-xl border border-slate-200 bg-white p-3"
              data-testid={`med-given-${g.id}`}
            >
              <div className="mb-2 flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm font-bold text-slate-900">
                  <Pill className="h-4 w-4 text-[#1D4ED8]" />
                  {g.name}
                  <span
                    className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${
                      g.indication === "prn"
                        ? "bg-amber-100 text-amber-800"
                        : "bg-blue-100 text-blue-800"
                    }`}
                  >
                    {indicationShort(g.indication)}
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 hover:bg-red-50"
                  onClick={() => removeEntry(g.id)}
                  data-testid={`med-given-delete-${g.id}`}
                >
                  <Trash2 className="h-4 w-4 text-red-600" />
                </Button>
              </div>
              <div className="grid gap-2 sm:grid-cols-4">
                <div>
                  <Label className="text-[11px] font-semibold uppercase text-slate-500">Hora</Label>
                  <Input
                    type="time"
                    value={g.time || ""}
                    onChange={(e) => updateEntry(g.id, { time: e.target.value })}
                    className="h-9 text-base tabular-nums"
                    data-testid={`med-given-time-${g.id}`}
                  />
                </div>
                <div>
                  <Label className="text-[11px] font-semibold uppercase text-slate-500">Dosis</Label>
                  <Input
                    value={g.dose || ""}
                    onChange={(e) => updateEntry(g.id, { dose: e.target.value })}
                    placeholder="500 mg"
                    className="h-9 text-base"
                    data-testid={`med-given-dose-${g.id}`}
                  />
                </div>
                <div>
                  <Label className="text-[11px] font-semibold uppercase text-slate-500">Via</Label>
                  <Select value={g.via} onValueChange={(v) => updateEntry(g.id, { via: v })}>
                    <SelectTrigger className="h-9 text-base" data-testid={`med-given-via-${g.id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {VIAS.map((v) => (
                        <SelectItem key={v} value={v}>{v}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[11px] font-semibold uppercase text-slate-500">Indicacion</Label>
                  <Select
                    value={g.indication}
                    onValueChange={(v) => updateEntry(g.id, { indication: v })}
                  >
                    <SelectTrigger className="h-9 text-base" data-testid={`med-given-ind-${g.id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {INDICATIONS.map((i) => (
                        <SelectItem key={i.value} value={i.value}>{i.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="mt-2 grid gap-2 sm:grid-cols-2">
                <div>
                  <Label className="text-[11px] font-semibold uppercase text-slate-500">
                    Administrado por
                  </Label>
                  <Input
                    value={g.administered_by || ""}
                    onChange={(e) => updateEntry(g.id, { administered_by: e.target.value })}
                    placeholder="Enfermera"
                    className="h-9 text-base"
                    data-testid={`med-given-by-${g.id}`}
                  />
                </div>
                <div>
                  <Label className="text-[11px] font-semibold uppercase text-slate-500">
                    Observaciones
                  </Label>
                  <Input
                    value={g.observations || ""}
                    onChange={(e) => updateEntry(g.id, { observations: e.target.value })}
                    placeholder="Tolero bien / reaccion / etc."
                    className="h-9 text-base"
                    data-testid={`med-given-obs-${g.id}`}
                  />
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Add new record */}
      {picking ? (
        <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid="med-pick-form">
          <Label className="text-xs font-bold uppercase tracking-wide text-slate-500">
            Selecciona un medicamento autorizado
          </Label>
          <div className="mt-2 flex flex-col gap-2 sm:flex-row">
            <Select value={selectedMedId} onValueChange={setSelectedMedId}>
              <SelectTrigger className="h-11 flex-1 text-base" data-testid="med-pick-select">
                <SelectValue placeholder="Elige del catalogo..." />
              </SelectTrigger>
              <SelectContent>
                {catalog.map((m) => {
                  const avail = prnAvailability(m, lastByMedCombined);
                  let suffix = "";
                  if (m.default_indication === "prn" && m.min_interval_hours) {
                    if (avail.lastEntry && !avail.allowed) {
                      suffix = ` - BLOQUEADO (faltan ${formatHours(avail.hoursUntilNext)})`;
                    } else if (avail.lastEntry) {
                      suffix = ` - disponible (ultima hace ${formatHours(avail.hoursSinceLast)})`;
                    } else {
                      suffix = " - disponible";
                    }
                  }
                  return (
                    <SelectItem
                      key={m.id}
                      value={m.id}
                      disabled={!avail.allowed}
                      data-testid={`med-pick-option-${m.id}`}
                    >
                      {avail.allowed ? null : <Ban className="mr-1 inline h-3 w-3 text-red-600" />}
                      {m.name}
                      {m.default_dose ? ` - ${m.default_dose}` : ""}
                      {` (${m.default_via || "-"})`}
                      {suffix}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            <Button
              onClick={() => addFromCatalog(selectedMedId)}
              disabled={!selectedMedId}
              className="h-11 rounded-xl bg-[#1D4ED8] font-semibold hover:bg-[#1E40AF]"
              data-testid="med-pick-confirm"
            >
              Agregar
            </Button>
            <Button
              variant="outline"
              onClick={() => { setPicking(false); setSelectedMedId(""); }}
              className="h-11 rounded-xl"
              data-testid="med-pick-cancel"
            >
              Cancelar
            </Button>
          </div>
        </div>
      ) : (
        <Button
          type="button"
          variant="outline"
          onClick={() => {
            if (empty) {
              toast.error(isAdmin
                ? "Primero autoriza al menos un medicamento en el catalogo"
                : "Pide al coordinador autorizar medicamentos para este paciente");
              return;
            }
            setPicking(true);
          }}
          disabled={noPatient}
          className="h-12 w-full rounded-xl border-dashed border-2 border-[#1D4ED8] text-base font-semibold text-[#1D4ED8] hover:bg-blue-50"
          data-testid="med-add-btn"
        >
          <Plus className="mr-2 h-5 w-5" />
          Registrar medicamento administrado
        </Button>
      )}
    </div>
  );
}
